/**
 * ScoreProfileChart — the radar of sites across criteria, as props (MYD-502).
 *
 * Pure: ScoreProfileCard feeds it on the Scoring tab and a presentation slide
 * feeds it from a resolved payload. `size="slide"` scales the type and fills
 * its box; the default is the Scoring tab's 260px card, unchanged.
 */

import { useMemo } from 'react'
import {
	Chart as ChartJS,
	RadialLinearScale,
	PointElement,
	LineElement,
	Filler,
	CategoryScale,
	LinearScale,
	BarElement,
	Tooltip,
	Legend,
} from 'chart.js'
import { Radar } from 'react-chartjs-2'

ChartJS.register(
	RadialLinearScale,
	PointElement,
	LineElement,
	Filler,
	CategoryScale,
	LinearScale,
	BarElement,
	Tooltip,
	Legend
)

export const SITE_COLORS = ['#0080FE', '#22C55E', '#F97316', '#A855F7', '#EC4899', '#14B8A6', '#EAB308', '#EF4444']

export interface ScoreProfileSite {
	name: string
	/** One per criterion, in the order of `criteria`; null where unscored. */
	values: Array<number | null>
}

interface ScoreProfileChartProps {
	criteria: string[]
	sites: ScoreProfileSite[]
	size?: 'card' | 'slide'
}

const ScoreProfileChart = ({ criteria, sites, size = 'card' }: ScoreProfileChartProps) => {
	const slide = size === 'slide'

	const data = useMemo(
		() => ({
			labels: criteria,
			datasets: sites.map((site, idx) => {
				const color = SITE_COLORS[idx % SITE_COLORS.length]
				return {
					label: site.name,
					data: site.values.map(value => value ?? 0),
					backgroundColor: `${color}33`,
					borderColor: color,
					borderWidth: slide ? 3 : 2,
					pointRadius: slide ? 4 : 2,
					pointBackgroundColor: color,
					pointBorderColor: '#fff',
				}
			}),
		}),
		[criteria, sites, slide]
	)

	const options = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			scales: {
				r: {
					min: 0,
					max: 10,
					ticks: {
						stepSize: 2,
						color: '#9ca3af',
						backdropColor: 'transparent' as const,
						font: { size: slide ? 14 : 9 },
					},
					grid: { color: '#e5e7eb' },
					angleLines: { color: '#e5e7eb' },
					pointLabels: { color: '#374151', font: { size: slide ? 17 : 9 } },
				},
			},
			plugins: {
				legend: {
					position: 'bottom' as const,
					labels: { color: '#6b7280', font: { size: slide ? 17 : 10 }, boxWidth: slide ? 14 : 8 },
				},
				tooltip: {
					callbacks: {
						label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.r}/10`,
					},
				},
			},
		}),
		[slide]
	)

	return (
		<div style={slide ? { height: '100%' } : { height: 260 }}>
			<Radar data={data} options={options} />
		</div>
	)
}

export default ScoreProfileChart
